import { Button, Col, Input, Row, Tabs } from "antd";
import { useTranslation } from "react-i18next";
import { SelectTerritorialUnits, SelectTypeTransport } from "../../../../components/selects";
import { CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import React, { useCallback, useEffect, useState } from "react";
import { EntLeasingItem } from "../../../../models/mechanization/leasingItem";
import { EntTransportItemLeasing } from "../../../../models/transportItemLeasing";

interface IProps {
    setItems: any;
    items: EntLeasingItem[] | null;
}

export const AddLeasingItems: React.FC<IProps> = ({ setItems, items }) => {
    const { t } = useTranslation();

    const [leasingItems, setLeasingItems] = useState<EntLeasingItem[]>([]);
    const [activeKey, setActiveKey] = useState(0);

    // Функция для генерации уникального ключа
    const generateUniqueKey = (): number => {
        return Math.random();
    };

    // Создание нового элемента с уникальным ключом
    const createNewItem = (): EntLeasingItem => ({
        key: generateUniqueKey(),
        id: null,
        territorialUnitId: null,
        territorialUnit: null,
        transports: [
            {
                id: null,
                transportTypeId: null,
                transportType: null,
                count: null,
                financingAmount: null,
            },
        ],
    });

    useEffect(() => {
        if (items && items.length > 0) {
            const itemsWithKeys = items.map((item) => ({
                ...item,
                key: item.key || generateUniqueKey(),
            }));
            setLeasingItems(itemsWithKeys);
        } else {
            setLeasingItems([createNewItem()]);
        }
    }, []);

    useEffect(() => {
        setItems(leasingItems);
    }, [leasingItems]);

    const handleAddItem = useCallback(() => {
        setLeasingItems((prevItems) => {
            const newItem = createNewItem();
            const newItems = [...prevItems, newItem];
            setActiveKey(newItems.length - 1); // Переключаемся на новый таб
            return newItems;
        });
    }, []);

    const handleDeleteItem = useCallback(
      (key: number) => {
          setLeasingItems((prevItems) => {
              const newItems = prevItems.filter((_, i) => i !== key);

              if (newItems.length === 0) return newItems;

              const newActiveKey = Math.max(0, key - 1);
              setActiveKey(newActiveKey);

              setItems(newItems);

              return newItems;
          });
      },
      []
    );

    const handleInputChange = (key: number, field: keyof EntLeasingItem, value: any) => {
        setLeasingItems((prevItems) =>
          prevItems.map((item) =>
            item.key === key ? { ...item, [field]: value } : item
          )
        );
        const updatedFertilizerUsageCrops = leasingItems.map((item) =>
          item.key === key ? { ...item, [field]: value } : item
        );
        setLeasingItems(updatedFertilizerUsageCrops);
        setItems(updatedFertilizerUsageCrops);
    };



    const handleTransportChange = (
        itemIndex: number,
        transportIndex: number,
        field: keyof EntTransportItemLeasing,
        value: any
    ) => {
        setLeasingItems((prevItems) =>
            prevItems.map((item, i) => {
                if (i === itemIndex) {
                    const updatedTransports = item.transports
                        ? [...item.transports]
                        : [];
                    updatedTransports[transportIndex] = {
                        ...updatedTransports[transportIndex],
                        [field]: value,
                    };
                    return { ...item, transports: updatedTransports };
                }
                return item;
            })
        );
    };



    const handleAddTransport = (itemIndex: number) => {
        setLeasingItems((prevItems) =>
            prevItems.map((item, i) => {
                if (i === itemIndex) {
                    const newTransport: EntTransportItemLeasing = {
                        id: null,
                        transportTypeId: null,
                        transportType: null,
                        count: null,
                        financingAmount: null,
                    };
                    return {
                        ...item,
                        transports: [...(item.transports || []), newTransport],
                    };
                }
                return item;
            })
        );
    };

    const isFieldEmpty = (value: any) => {
        return value === null || value === undefined || value === "";
    };

    const handleDeleteTransport = (itemIndex: number, transportIndex: number) => {
        setLeasingItems((prevItems) =>
            prevItems.map((item, i) => {
                if (i === itemIndex) {
                    if (item.transports && item.transports.length > 1) {
                        const updatedTransports = item.transports.filter(
                            (_, index) => index !== transportIndex
                        );
                        return { ...item, transports: updatedTransports };
                    }
                }
                return item;
            })
        );
    };

    return (
        <div style={{ marginTop: 15 }}>
            <Tabs
                type="editable-card"
                activeKey={activeKey.toString()}
                onChange={(key) => setActiveKey(Number(key))} 
                onEdit={(key, action) => {
                    if (action === "add") {
                        handleAddItem();
                    } else if (action === "remove") {
                        handleDeleteItem(Number(key)); 
                    }
                }}
            >
                {leasingItems.map((item, index) => (
                    <Tabs.TabPane
                        key={index.toString()} 
                        tab={`Терр. единица ${index + 1}`}
                        closable={leasingItems.length > 1}
                    >
                        <div className="block_item_form massive_blocks">
                            <Row style={{ margin: 0 }} gutter={16}>
                                <Col className="gutter-row" span={24}>
                                    <label>{t('general.territorial_unit')}</label>
                                    <SelectTerritorialUnits
                                        type={ ['REGION'] }
                                        value={item.territorialUnitId}
                                        onChange={(value) =>
                                            handleInputChange(item.key as number, "territorialUnitId", value)
                                        }
                                        status={isFieldEmpty(item.territorialUnitId) ? "error" : ""}
                                    />
                                </Col>
                                <Col className="gutter-row" span={24}>
                                    {item.transports?.map((transport, transportIndex) => (
                                        <div key={transportIndex} className="block_item_form list_in_linst">
                                            <Row style={{ margin: 0 }} gutter={16}>
                                                <Col className="gutter-row" span={12}>
                                                    <label>{t('titles.type_transport')}</label>
                                                    <SelectTypeTransport
                                                        value={transport.transportTypeId}
                                                        onChange={(value) =>
                                                            handleTransportChange(
                                                                index,
                                                                transportIndex,
                                                                "transportTypeId",
                                                                value
                                                            )
                                                        }
                                                        status={isFieldEmpty(transport.transportTypeId) ? "error" : ""}
                                                    />
                                                </Col>
                                                <Col className="gutter-row" span={12}>
                                                    <label>{t('mechanisation.leasing.technical_count')}</label>
                                                    <Input
                                                        type="text"
                                                        value={transport.count || ""}
                                                        onChange={(e) =>
                                                            handleTransportChange(
                                                                index,
                                                                transportIndex,
                                                                "count",
                                                                e.target.value
                                                            )
                                                        }
                                                        className={isFieldEmpty(transport.count) ? "error-border" : ""}
                                                    />
                                                </Col>
                                                <Col className="gutter-row" span={12}>
                                                    <label>{t('mechanisation.leasing.amount_financing')}</label>
                                                    <Input
                                                        type="text"
                                                        value={transport.financingAmount || ""}
                                                        onChange={(e) =>
                                                            handleTransportChange(
                                                                index,
                                                                transportIndex,
                                                                "financingAmount",
                                                                e.target.value
                                                            )
                                                        }
                                                        className={isFieldEmpty(transport.financingAmount) ? "error-border" : ""}
                                                    />
                                                </Col>
                                           
                                                    <Button
                                                        className="fixed_delete"
                                                        type="primary"
                                                        title="Удалить"
                                                        danger
                                                        icon={<CloseOutlined />}
                                                        shape="circle"
                                                        size="small"
                                                        onClick={() => handleDeleteTransport(index, transportIndex)}
                                                        disabled={!!item.transports && item.transports.length <= 1}
                                                    />
                                            
                                            </Row>
                                        </div>
                                    ))}
                                    <Button
                                        style={{ marginTop: 15 }}
                                        type="primary"
                                        onClick={() => handleAddTransport(index)}
                                    >
                                        {t("Добавить транспорт")}
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </Tabs.TabPane>
                ))}
            </Tabs>
            <Button
                style={{ marginTop: 15 }}
                icon={<PlusCircleOutlined />}
                type="primary"
                onClick={handleAddItem}
            >
                {t("Добавить еще территориальную единицу")}
            </Button>
        </div>
    );
};